import React, { useState, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, AlertTriangle, Camera, FileText, CheckCircle2, AlertCircle, XCircle, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isAllowedImageType, ACCEPTED_IMAGE_EXTENSIONS } from '@/lib/image-validation';
import type { AssetAuditProgress } from '@/types/audit';

interface EditAssetSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: AssetAuditProgress;
  onSave: (assetId: string, updates: Partial<AssetAuditProgress>) => void;
}

export function EditAssetSheet({
  open,
  onOpenChange,
  asset,
  onSave,
}: Readonly<EditAssetSheetProps>) {
  const [editedLocation, setEditedLocation] = useState(asset.currentLocation);
  const [editedCondition, setEditedCondition] = useState<'good' | 'attention' | 'critical' | undefined>(asset.condition);
  const [editedNotes, setEditedNotes] = useState(asset.notes ?? '');
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when asset changes or sheet opens
  React.useEffect(() => {
    if (open) {
      setEditedLocation(asset.currentLocation);
      setEditedCondition(asset.condition);
      setEditedNotes(asset.notes ?? '');
      setUploadedPhotos([]);
    }
  }, [open, asset]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => isAllowedImageType(file));
    setUploadedPhotos(prev => [...prev, ...imageFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => isAllowedImageType(file));
    setUploadedPhotos(prev => [...prev, ...imageFiles]);
  };

  const handleRemovePhoto = (index: number) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    onSave(asset.id, {
      currentLocation: editedLocation,
      condition: editedCondition,
      notes: editedNotes,
      // TODO: Handle photo upload to backend
      // photos: uploadedPhotos,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to original values
    setEditedLocation(asset.currentLocation);
    setEditedCondition(asset.condition);
    setEditedNotes(asset.notes ?? '');
    setUploadedPhotos([]);
    onOpenChange(false);
  };

  // Condition options matching the AssetAuditProgress type
  const conditionOptions: Array<{
    value: 'good' | 'attention' | 'critical';
    label: string;
    icon: typeof CheckCircle2;
    color: string;
    description: string;
  }> = [
    {
      value: 'good',
      label: 'Good',
      icon: CheckCircle2,
      color: 'emerald',
      description: 'Asset is in good condition'
    },
    {
      value: 'attention',
      label: 'Needs Attention',
      icon: AlertCircle,
      color: 'amber',
      description: 'Minor issues or maintenance required'
    },
    {
      value: 'critical',
      label: 'Critical',
      icon: XCircle,
      color: 'red',
      description: 'Significant damage or issues'
    },
  ];

  const getConditionConfig = (value: string) => {
    return conditionOptions.find(opt => opt.value === value) || conditionOptions[0];
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] flex flex-col p-0">
        {/* Header with background */}
        <SheetHeader className="px-6 py-4 bg-white dark:bg-black border-b">
          <SheetTitle className="text-xl">
            {asset.assetName}
          </SheetTitle>
          <SheetDescription className="text-base">
            {asset.assetCode} • {asset.category}
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable Content with padding */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Location Field */}
          <div className="space-y-2">
            <Label htmlFor="location" className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Location
            </Label>
            <Select value={editedLocation} onValueChange={setEditedLocation}>
              <SelectTrigger id="location">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Building A - Floor 1">Building A - Floor 1</SelectItem>
                <SelectItem value="Building A - Floor 2">Building A - Floor 2</SelectItem>
                <SelectItem value="Building B - Floor 1">Building B - Floor 1</SelectItem>
                <SelectItem value="Building B - Floor 2">Building B - Floor 2</SelectItem>
                <SelectItem value="Warehouse">Warehouse</SelectItem>
                <SelectItem value="Storage Room">Storage Room</SelectItem>
                <SelectItem value="Server Room">Server Room</SelectItem>
                <SelectItem value="Reception">Reception</SelectItem>
              </SelectContent>
            </Select>
            {asset.previousLocation && editedLocation !== asset.previousLocation && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Previously: {asset.previousLocation}
              </p>
            )}
          </div>

          {/* Condition Field */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              Condition
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {conditionOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = editedCondition === option.value;
                
                // Map colors to explicit Tailwind classes for JIT compilation
                const colorClasses = {
                  emerald: {
                    border: 'border-emerald-500',
                    bg: 'bg-emerald-50 dark:bg-emerald-950',
                    icon: 'text-emerald-600 dark:text-emerald-400',
                    text: 'text-emerald-700 dark:text-emerald-300',
                    desc: 'text-emerald-600 dark:text-emerald-400',
                  },
                  amber: {
                    border: 'border-amber-500',
                    bg: 'bg-amber-50 dark:bg-amber-950',
                    icon: 'text-amber-600 dark:text-amber-400',
                    text: 'text-amber-700 dark:text-amber-300',
                    desc: 'text-amber-600 dark:text-amber-400',
                  },
                  red: {
                    border: 'border-red-500',
                    bg: 'bg-red-50 dark:bg-red-950',
                    icon: 'text-red-600 dark:text-red-400',
                    text: 'text-red-700 dark:text-red-300',
                    desc: 'text-red-600 dark:text-red-400',
                  },
                  slate: {
                    border: 'border-slate-500',
                    bg: 'bg-slate-50 dark:bg-slate-950',
                    icon: 'text-slate-600 dark:text-slate-400',
                    text: 'text-slate-700 dark:text-slate-300',
                    desc: 'text-slate-600 dark:text-slate-400',
                  },
                  orange: {
                    border: 'border-orange-500',
                    bg: 'bg-orange-50 dark:bg-orange-950',
                    icon: 'text-orange-600 dark:text-orange-400',
                    text: 'text-orange-700 dark:text-orange-300',
                    desc: 'text-orange-600 dark:text-orange-400',
                  },
                };
                
                const colors = colorClasses[option.color as keyof typeof colorClasses];
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setEditedCondition(option.value)}
                    className={cn(
                      'flex flex-col items-start gap-2 p-3 rounded-lg border-2 transition-all text-left',
                      isSelected
                        ? `${colors.border} ${colors.bg}`
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-black hover:border-slate-300 dark:hover:border-slate-600'
                    )}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Icon className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isSelected ? colors.icon : 'text-slate-400'
                      )} />
                      <span className={cn(
                        'text-sm font-medium',
                        isSelected ? colors.text : 'text-slate-700 dark:text-slate-300'
                      )}>
                        {option.label}
                      </span>
                    </div>
                    <p className={cn(
                      'text-xs',
                      isSelected ? colors.desc : 'text-slate-500 dark:text-slate-400'
                    )}>
                      {option.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Photos Field */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-muted-foreground" />
              Photos
            </Label>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_EXTENSIONS}
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Upload area */}
            <button
              type="button"
              onClick={handleUploadClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                "w-full border-2 border-dashed rounded-lg p-8 text-center bg-white dark:bg-black transition-colors cursor-pointer",
                isDragging 
                  ? "border-primary bg-primary/5" 
                  : "hover:border-primary/50"
              )}
            >
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium mb-1">Upload Photos</p>
              <p className="text-xs text-muted-foreground">
                Click to browse or drag and drop
              </p>
              {asset.photos && asset.photos.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {asset.photos.length} existing photo{asset.photos.length !== 1 ? 's' : ''}
                </p>
              )}
            </button>

            {/* Preview uploaded photos */}
            {uploadedPhotos.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {uploadedPhotos.length} new photo{uploadedPhotos.length !== 1 ? 's' : ''} to upload
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {uploadedPhotos.map((file, fileIndex) => (
                    <div key={file.name} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Upload ${file.name}`}
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(fileIndex);
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {file.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Notes
            </Label>
            <Textarea
              id="notes"
              value={editedNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditedNotes(e.target.value)}
              placeholder="Add notes about this asset..."
              rows={6}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {editedNotes.length} characters
            </p>
          </div>
        </div>

        {/* Footer with Actions and background */}
        <SheetFooter className="px-6 py-4 bg-white dark:bg-black border-t">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
